<template>
  <div class="dt-root">
    <!-- Toolbar: left slot · row count · Save View / Reset · Export CSV/PDF -->
    <div v-if="showToolbar" class="dt-toolbar">
      <div class="dt-toolbar-left">
        <slot name="toolbar-left" />
      </div>
      <div class="dt-toolbar-right">
        <span class="dt-count">
          {{ viewRows.length.toLocaleString() }}<span v-if="viewRows.length !== rows.length"> of {{ rows.length.toLocaleString() }}</span>
          {{ viewRows.length === 1 ? 'row' : 'rows' }}
        </span>
        <slot name="toolbar-right" />
        <template v-if="savedViewKey">
          <button class="dt-btn" @click="$emit('save-view')">{{ savedJustSaved ? 'Saved ✓' : 'Save View' }}</button>
          <button v-if="hasSaved" class="dt-btn" @click="$emit('reset-view')">Reset</button>
        </template>
        <ExportMenu v-if="exportCsvEnabled || exportPdfEnabled" size="sm" :disabled="!viewRows.length" :exports="[
          ...(exportCsvEnabled ? [{ type: 'csv', action: doExportCsv }] : []),
          ...(exportPdfEnabled ? [{ type: 'pdf', action: doExportPdf }] : []),
        ]" />
      </div>
    </div>

    <div class="ext-table-wrap dt-wrap" :style="{ maxHeight: maxHeight }">
      <table class="ext-table dt-table" :style="{ whiteSpace: 'nowrap' }">
        <thead>
          <tr ref="headerRow">
            <th
              v-for="col in columns"
              :key="col.field"
              :class="[{ num: isNum(col), sortable: col.sortable !== false, 'dt-sticky': col.sticky }]"
              :style="col.width ? { width: col.width + 'px', maxWidth: col.width + 'px' } : null"
              :title="col.headerTooltip || null"
              @click="col.sortable !== false && toggleSort(col.field)"
            >
              <span class="dt-th-label">{{ col.header }}</span>
              <span v-if="col.sortable !== false" class="dt-sort-ind">{{ sortIcon(col.field) }}</span>
            </th>
          </tr>
          <!-- Filter row (only if any column is filterable) -->
          <tr v-if="hasFilterableCols" class="dt-filter-row">
            <th v-for="col in columns" :key="'f-' + col.field" :class="{ 'dt-sticky': col.sticky }">
              <template v-if="col.filterable">
                <template v-if="isNum(col)">
                  <div class="dt-filter-num">
                    <select v-model="filters[col.field].op" class="dt-filter-op">
                      <option value="">—</option>
                      <option value="gte">≥</option>
                      <option value="lte">≤</option>
                      <option value="eq">=</option>
                      <option value="between">range</option>
                    </select>
                    <template v-if="filters[col.field].op === 'between'">
                      <input v-model="filters[col.field].min" type="number" class="dt-filter-input" placeholder="min">
                      <input v-model="filters[col.field].max" type="number" class="dt-filter-input" placeholder="max">
                    </template>
                    <input
                      v-else-if="filters[col.field].op"
                      v-model="filters[col.field].value" type="number" class="dt-filter-input" placeholder="value"
                    >
                  </div>
                </template>
                <template v-else>
                  <input
                    v-model="filters[col.field].text" type="text" class="dt-filter-input"
                    :class="{ sensitive: isSensitiveColumn(col) }" :data-demo-search="isSensitiveColumn(col) ? '' : null" placeholder="contains…"
                  >
                </template>
              </template>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, i) in viewRows"
            :key="rowKey ? row[rowKey] : i"
            :class="[{ 'dt-clickable': !!$attrs.onRowClick }, rowClass ? rowClass(row) : null]"
            @click="$emit('row-click', row)"
          >
            <td
              v-for="col in columns"
              :key="col.field"
              :class="[{ num: isNum(col), 'dt-sticky': col.sticky }, col.cellClass ? col.cellClass(row[col.field], row) : null]"
              :style="col.cellStyle ? col.cellStyle(row[col.field], row) : null"
            >
              <slot v-if="col.cell" :name="'cell-' + col.field" :row="row" :value="row[col.field]" :col="col" />
              <template v-else>{{ fmt(col, row) }}</template>
            </td>
          </tr>
          <tr v-if="!viewRows.length">
            <td :colspan="columns.length" class="dt-empty">{{ emptyText }}</td>
          </tr>
          <tr v-if="showTotals && viewRows.length > 1" class="dt-total-row">
            <td v-for="(col, ci) in columns" :key="'t-' + col.field" :class="{ num: isNum(col) }">
              <template v-if="ci === 0">Σ shown</template>
              <template v-else-if="isNum(col) && col.total !== false">{{ fmtTotal(col) }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import './extTable.css'
import { sortRows, filterRows, isNumericType } from './tableHelpers'
import { formatCell, formatCurrency, formatNumber, formatPer1k } from './tableFormat'
import { exportCsv, exportPdf } from './tableExport'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import demoProtectedFields from '@/mixins/demoProtectedFields'

// A column is sensitive when its cells are tagged: `sensitive: true`, or a
// cellClass (string or function) that yields 'sensitive'. A cellClass that
// throws on an empty row cannot be judged, so it counts as sensitive.
export function isSensitiveColumn(col) {
  if (!col) return false
  if (col.sensitive === true) return true
  let cls = col.cellClass
  if (typeof cls === 'function') {
    try {
      cls = cls(undefined, {})
    } catch {
      return true
    }
  }
  if (Array.isArray(cls)) cls = cls.join(' ')
  if (cls && typeof cls === 'object') cls = Object.keys(cls).filter(k => cls[k]).join(' ')
  return typeof cls === 'string' && cls.split(/\s+/).includes('sensitive')
}

/**
 * DataTable — the unified hand-rolled table for ordinary External lists.
 * Owns sort + column-filter state; delegates the actual sort/filter/format/export
 * to the shared helpers so bespoke tables use the SAME logic.
 *
 * SAVED VIEWS: the parent owns persistence (via the savedView mixin) because the
 * mixin restores primitive data() fields. DataTable exposes its sort/filter state
 * through v-model:sortModel / v-model:filters so the parent can list them in its
 * savedView.fields and wire Save/Reset buttons (emitted up).
 */
export default {
  name: 'ExternalDataTable',
  components: { ExportMenu },
  mixins: [demoProtectedFields],
  // A text filter on a tagged column (patient name, MBI) holds a real
  // identifying term. Demo Mode empties it and the tagged filter input is
  // read-only; filters on untagged columns stay usable.
  demoProtectedFields() {
    return this.columns
      .filter(c => c.filterable && !isNumericType(c.type) && isSensitiveColumn(c))
      .map(c => ['filters', c.field, 'text'])
  },
  inheritAttrs: false,
  props: {
    columns: { type: Array, required: true },
    rows: { type: Array, default: () => [] },
    rowKey: { type: String, default: null },
    // optional (row) => class string/object for full-row styling (e.g. readmit highlight)
    rowClass: { type: Function, default: null },
    // export config: true (both), false (none), or { csv, pdf, filename, title, subtitle, orientation }
    exportConfig: { type: [Boolean, Object], default: true },
    // saved-view controls shown when a key is provided (parent owns persistence)
    savedViewKey: { type: String, default: null },
    hasSaved: { type: Boolean, default: false },
    savedJustSaved: { type: Boolean, default: false },
    // controlled sort/filter (optional — else internal)
    sortModel: { type: Object, default: null },
    filterModel: { type: Object, default: null },
    showToolbar: { type: Boolean, default: true },
    showTotals: { type: Boolean, default: false },
    maxHeight: { type: String, default: '70vh' },
    emptyText: { type: String, default: 'No data.' },
    exportTitle: { type: String, default: 'Report' },
    exportSubtitle: { type: String, default: '' },
    exportFilename: { type: String, default: 'export' },
  },
  emits: ['row-click', 'save-view', 'reset-view', 'update:sortModel', 'update:filterModel'],
  data() {
    return {
      internalSort: this.sortModel || { key: null, dir: 'asc' },
      filters: {},
    }
  },
  computed: {
    sort() { return this.sortModel || this.internalSort },
    hasFilterableCols() { return this.columns.some(c => c.filterable) },
    exportCsvEnabled() {
      const e = this.exportConfig
      return e === true || (e && e.csv !== false)
    },
    exportPdfEnabled() {
      const e = this.exportConfig
      return e === true || (e && e.pdf !== false)
    },
    viewRows() {
      const filtered = filterRows(this.rows, this.filters, this.columns)
      return sortRows(filtered, this.sort, this.columns)
    },
  },
  watch: {
    columns: { immediate: true, handler() { this.syncFilters(); this.$nextTick(this.measureHeader) } },
    filterModel: { handler(v) { if (v) this.filters = v }, immediate: true },
    filters: { deep: true, handler(v) { this.$emit('update:filterModel', v) } },
  },
  mounted() {
    this.measureHeader()
    if (typeof ResizeObserver !== 'undefined' && this.$refs.headerRow) {
      this._headerRO = new ResizeObserver(() => this.measureHeader())
      this._headerRO.observe(this.$refs.headerRow)
    }
  },
  beforeUnmount() {
    if (this._headerRO) this._headerRO.disconnect()
  },
  methods: {
    isNum(col) { return isNumericType(col.type) },
    isSensitiveColumn,
    // The filter row sticks BELOW the header row, so its `top` must be the
    // header's real rendered height — a hardcoded value breaks whenever the
    // header wraps to two lines or the font scale changes.
    measureHeader() {
      const h = this.$refs.headerRow?.offsetHeight
      if (h) this.$el?.style.setProperty('--dt-header-h', `${h}px`)
    },
    fmt(col, row) { return formatCell(col, row) },
    // Ensure every filterable column has a filter slot in the reactive map.
    syncFilters() {
      const next = this.filterModel ? { ...this.filterModel } : { ...this.filters }
      for (const c of this.columns) {
        if (!c.filterable) continue
        if (!next[c.field]) {
          next[c.field] = isNumericType(c.type)
            ? { op: '', value: '', min: '', max: '' }
            : { text: '', selected: [] }
        }
      }
      this.filters = next
    },
    toggleSort(field) {
      const s = this.sort
      let nextSort
      if (s.key !== field) nextSort = { key: field, dir: 'asc' }
      else if (s.dir === 'asc') nextSort = { key: field, dir: 'desc' }
      else nextSort = { key: null, dir: 'asc' }  // third click clears
      if (this.sortModel) this.$emit('update:sortModel', nextSort)
      else this.internalSort = nextSort
    },
    sortIcon(field) {
      const s = this.sort
      if (s.key !== field) return '↕'
      return s.dir === 'asc' ? '↑' : '↓'
    },
    fmtTotal(col) {
      const sum = this.viewRows.reduce((a, r) => {
        const n = Number(r[col.field])
        return a + (Number.isNaN(n) ? 0 : n)
      }, 0)
      if (col.type === 'currency') return formatCurrency(sum)
      if (col.type === 'per_1k') return formatPer1k(sum)
      return formatNumber(sum)
    },
    doExportCsv() {
      exportCsv(this.viewRows, this.columns, { filename: `${this.exportFilename}.csv` })
    },
    doExportPdf() {
      exportPdf(this.viewRows, this.columns, {
        filename: `${this.exportFilename}.pdf`,
        title: this.exportTitle,
        subtitle: this.exportSubtitle,
      })
    },
  },
}
</script>

<style scoped>
.dt-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin-bottom: 8px; flex-wrap: wrap;
}
.dt-toolbar-left, .dt-toolbar-right { display: flex; align-items: center; gap: 8px; }
.dt-count { font-size: 11px; color: #7D7770; }
.dt-btn {
  font-family: inherit; font-size: 11.5px; color: #4A4540;
  background: #FFFFFF; border: 1px solid #E8E3DC; border-radius: 6px;
  padding: 4px 10px; cursor: pointer;
}
.dt-btn:hover:not(:disabled) { border-color: #CFC8BE; background: #FAF9F6; }
.dt-btn:disabled { opacity: 0.5; cursor: default; }

.dt-wrap { overflow: auto; }
.dt-table { border-collapse: separate; border-spacing: 0; }
.dt-table thead th {
  position: sticky; top: 0; z-index: 10;
  background: #FAF9F6;
  box-shadow: inset 0 -1px 0 #E8E3DC;
}
.dt-table thead tr.dt-filter-row th {
  top: var(--dt-header-h, 33px); z-index: 10;
}
.ext-table th.sortable { cursor: pointer; user-select: none; white-space: nowrap; }
.ext-table th.sortable:hover { background: #F3F1ED; }
.dt-th-label { vertical-align: middle; }
.dt-sort-ind { margin-left: 4px; font-size: 9px; color: #A9A29A; }
.dt-sticky { position: sticky; left: 0; z-index: 2; background: #FFFFFF; }
.dt-table thead th.dt-sticky { background: #FAF9F6; z-index: 11; }

.dt-filter-row th { padding: 4px 6px; border-bottom: 1px solid #F2EEE8; }
.dt-filter-num { display: flex; gap: 3px; }
.dt-filter-op {
  font-size: 10px; border: 1px solid #E8E3DC; border-radius: 4px;
  padding: 2px 3px; background: #fff; color: #4A4540;
}
.dt-filter-input {
  font-size: 10px; border: 1px solid #E8E3DC; border-radius: 4px;
  padding: 2px 5px; width: 100%; min-width: 44px; max-width: 90px; color: #221F1C;
}
.dt-filter-input:focus, .dt-filter-op:focus { outline: none; border-color: #CFC8BE; }

.dt-clickable { cursor: pointer; }
.dt-empty { text-align: center; color: #A9A29A; padding: 24px 0; font-size: 12px; }
.dt-total-row td {
  font-weight: 600; border-top: 1px solid #E8E3DC;
  background: #FAF9F6 !important; color: #221F1C;
}
</style>
